import { Box, Button, HStack, Spacer } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import MDSpinner from "react-md-spinner";
import { useSelector } from "react-redux";

import NumberInput from "../components/NumberInput";
import { checkPermission } from "../state/appState";
import {
  getCurrentAssetProperty,
  getExistingTimedStructureControls,
  submitPropertyEdit,
} from "../state/existingEvents";
import { getSimulation, getTime } from "../state/simulation";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import inputElementStyles from "../styles/InputElements.module.css";
import { Asset, PropertyName, UnknownAssetProperties } from "../types/assets";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";

import { EditType, PropertyInformation } from "./AssetProperties";
import editStyles from "./EditPropertyInfos.module.css";
import menuStyles from "./Menu.module.css";

interface Props {
  asset: Asset;
  propertyInfos: PropertyInformation[];
  close: () => void;
}

interface EditSingleProps {
  propertyInfo: PropertyInformation;
  value: number | string | undefined;
  originalValue: number | undefined;
  edit: EditType;
  setValue: (newValue: number | string) => void;
}

const EditPropertyInfo: React.FC<EditSingleProps> = ({
  propertyInfo,
  value,
  originalValue,
  edit,
  setValue,
}) => {
  const PrettyName = propertyInfo.prettyName;

  let input: Element | JSX.Element | null = null;

  if (edit === "setValue") {
    input = (
      <NumberInput
        value={value !== undefined ? "" + value : " "}
        unit={propertyInfo.unit ? propertyInfo.unit : ""}
        allowDecimals
        allowNegative
        setValue={setValue}
        required
      />
    );
  } else if (edit === "openclose") {
    input = (
      <select
        className={`${inputElementStyles.Padding} ${inputElementStyles.Input}`}
        value={value === 0 ? "closed" : "open"}
        onChange={(event) =>
          setValue(event.target.value === "open" ? (originalValue ? originalValue : 1) : 0)
        }
      >
        <option value="open">Open</option>
        <option value="closed">Close</option>
      </select>
    );
  }

  return (
    <HStack>
      <span>
        {edit === "openclose" ? (
          <FormattedMessage id="oneDElementInspector.status" defaultMessage="Status" />
        ) : PrettyName ? (
          <PrettyName />
        ) : (
          propertyInfo.property
        )}
      </span>
      <Spacer />
      {input}
    </HStack>
  );
};

export const EditPropertyInfos: React.FC<Props> = (props: Props) => {
  const beforeEditedProps: { [key: string]: number | string | undefined } = {};
  const originalProps: { [key: string]: number } = {};

  const { asset, propertyInfos, close } = props;

  const simulation = useSelector(getSimulation);
  const time = useSelector(getTime);
  const existingTimedStructureControls = useSelector(getExistingTimedStructureControls);

  const simulationId = simulation.id!;

  const permission = useSelector(checkPermission)("editAssetProperty");
  const disableWholeForm = permission.allowed === false;
  const intl = useIntl();

  const disablesWholeFormErrorMessage =
    permission.allowed === false && formattedMessageForErrorCode(permission.errorCode, intl);

  for (const propertyInfo of propertyInfos) {
    const prop: PropertyName = propertyInfo.property;
    const value = getCurrentAssetProperty(asset, existingTimedStructureControls, time, prop);

    beforeEditedProps[prop] = value ? Number(value).toFixed(2) : undefined;

    originalProps[prop] = (asset.properties as unknown as UnknownAssetProperties)[prop!];
  }

  const [submitting, setSubmitting] = useState(false);
  const [currentProps, setCurrentProps] = useState(beforeEditedProps);

  const doSubmit = async () => {
    setSubmitting(true);

    await Promise.all(
      propertyInfos.map((propertyInfo) => {
        const currentPropsProperty = currentProps[propertyInfo.property];
        if (
          currentPropsProperty !== undefined &&
          currentPropsProperty !== beforeEditedProps[propertyInfo.property]
        ) {
          return submitPropertyEdit(
            simulationId,
            time,
            existingTimedStructureControls,
            asset,
            propertyInfo.property,
            +currentPropsProperty,
            originalProps[propertyInfo.property]
          );
        } else {
          // Do nothing, value didn't change
          return Promise.resolve();
        }
      })
    );

    setSubmitting(false);
    close();
  };

  if (submitting) {
    return (
      <MDSpinner
        className={editStyles.SpinnerStyle}
        singleColor={getComputedStyle(document.documentElement).getPropertyValue(
          "--gui-action-element-hover-color"
        )}
      />
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className={FormStyles.NormalForm}
    >
      <fieldset disabled={disableWholeForm}>
        <Box margin="8px 0">
          {propertyInfos.map((propertyInfo) => {
            return (
              propertyInfo.edits &&
              propertyInfo.edits.map((edit) => (
                <EditPropertyInfo
                  key={propertyInfo.property + edit}
                  propertyInfo={propertyInfo}
                  edit={edit}
                  value={currentProps[propertyInfo.property]}
                  originalValue={originalProps[propertyInfo.property]}
                  setValue={(newValue: number | string) => {
                    setCurrentProps({
                      ...currentProps,
                      [propertyInfo.property]: newValue,
                    });
                  }}
                />
              ))
            );
          })}
        </Box>
        <HStack margin="8px 0">
          <Spacer />
          <Button variant="secondary" onClick={close}>
            <FormattedMessage id="oneDElementInspector.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            title={disablesWholeFormErrorMessage || undefined}
            variant="primary"
            onClick={(e) => {
              const target = e.target as HTMLButtonElement;
              if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                doSubmit();
              }
            }}
            onMouseOver={(e) => {
              // this clicks the button onhover when it is invalid  so the invalid messages show on the fields
              const target = e.target as HTMLButtonElement;
              if (!(target.form && target.form.checkValidity && target.form.checkValidity())) {
                target.click();
              }
            }}
          >
            <FormattedMessage id="oneDElementInspector.confirm" defaultMessage="CONFIRM" />
          </Button>
        </HStack>
        <div className={`${menuStyles.ListItem} `}>
          {disablesWholeFormErrorMessage ? (
            <span className={ErrorMessageStyles.ErrorMessageStyle}>
              {disablesWholeFormErrorMessage}
            </span>
          ) : null}
        </div>
      </fieldset>
    </form>
  );
};
